// src/components/Finanzas/InformeFinanciero.jsx
// Informe gerencial del modulo financiero (011 - Bolsa de Fondos).
// Reutiliza el layout y estilos del ReporteViaticos viejo, pero conectado al
// modelo nuevo (EntregaDinero + GastoContable + TrasladoDinero + RelacionGastos).
//
// Visibilidad: backend lo limita a Admin / Gerente / Contador.
//
// Secciones:
//   1. Filtros: fechas + responsables + cuentas contables + OT + estado gasto.
//   2. KPIs (6 cards): entregado, gastado, traslados, saldo, % ejecucion, dias cierre.
//   3. Alertas (banner) — analista financiero.
//   4. Tendencia mensual (linea).
//   5. Entregas vs Gastos por responsable (tabla).
//   6. Gastos por cuenta contable + por categoria (tablas).
//   7. Top proveedores.
//   8. Traslados entre cuentas / bolsillos.

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Badge, Alert, Spinner, ProgressBar, Accordion } from 'react-bootstrap';
import Select from 'react-select';
import {
  FaFileInvoiceDollar, FaDownload, FaPrint, FaCalendarAlt, FaExclamationTriangle,
  FaUsers, FaArrowUp, FaArrowDown, FaExchangeAlt, FaClock, FaChartLine, FaBuilding,
  FaFolderOpen
} from 'react-icons/fa';
import {
  Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale,
  BarElement, LineElement, PointElement, Title, Filler
} from 'chart.js';
import { Line, Pie } from 'react-chartjs-2';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Alertassw } from '../Alertas/Alertassw';
import { generarInformeFinanciero, cuentasFuenteFondos, cuentasBolsillosPersonales } from '../../api/finanzas';
import { filterData } from '../../api/common';
import SelectorOT from './SelectorOT';

ChartJS.register(
  ArcElement, Tooltip, Legend, CategoryScale, LinearScale,
  BarElement, LineElement, PointElement, Title, Filler
);

const formatoCOP = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;
const formatoMes = (m) => {
  const [y, mm] = m.split('-');
  const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  return `${meses[parseInt(mm,10) - 1]} ${y}`;
};

const ESTADOS_GASTO = [
  { value: '', label: 'Todos (excepto Anulado)' },
  { value: 'REGISTRADO', label: 'Registrados (activos)' },
  { value: 'INCLUIDO_EN_RELACION', label: 'En relación' },
  { value: 'ANULADO', label: 'Anulados' }
];

const ALERT_VARIANT = {
  warning: 'warning',
  danger: 'danger',
  info: 'info'
};

const InformeFinanciero = () => {
  const [filtros, setFiltros] = useState({
    fechaDesde: '',
    fechaHasta: '',
    responsableIds: [],
    cuentaContableIds: [],
    otSeleccionada: null,
    estadoGasto: ''
  });

  const [responsablesOpt, setResponsablesOpt] = useState([]);
  const [cuentasOpt, setCuentasOpt]           = useState([]);
  const [datos, setDatos]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [cargandoCatalogos, setCargandoCatalogos] = useState(true);

  // ============================================
  // Carga inicial de catalogos
  // ============================================
  useEffect(() => {
    cargarCatalogos();
    generar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarCatalogos = async () => {
    setCargandoCatalogos(true);
    try {
      // Usuarios activos (no Cliente) — mismo aggregate que CuentasContablesForm
      const usuariosResp = await filterData('usuarios', [
        { $match: { status: 'activo' } },
        { $lookup: { from: 'roles', localField: 'role', foreignField: '_id', as: 'roleData' } },
        { $match: { 'roleData.nombre': { $ne: 'Cliente' } } }
      ]);
      const usuarios = usuariosResp?.data?.data || [];
      setResponsablesOpt(usuarios.map(u => ({
        value: u._id,
        label: `${u.nombre || ''} ${u.apellido || ''}`.trim() || u.email
      })));

      // Cuentas: fuente de fondos + bolsillos personales (las relevantes al informe)
      const [fuente, bolsillos] = await Promise.all([
        cuentasFuenteFondos(),
        cuentasBolsillosPersonales()
      ]);
      const cuentas = [
        ...(fuente?.data?.data || []),
        ...(bolsillos?.data?.data || [])
      ];
      // De-dup por _id
      const map = new Map();
      cuentas.forEach(c => { if (!map.has(c._id)) map.set(c._id, c); });
      setCuentasOpt(Array.from(map.values()).map(c => ({
        value: c._id,
        label: `${c.codigo} — ${c.nombre}`
      })));
    } catch (err) {
      console.error('Error cargar catalogos informe:', err);
    } finally {
      setCargandoCatalogos(false);
    }
  };

  const handleFiltro = (name, value) => {
    setFiltros(prev => ({ ...prev, [name]: value }));
  };

  // ============================================
  // Generar informe
  // ============================================
  const generar = async () => {
    setLoading(true);
    try {
      const payload = {
        fechaDesde: filtros.fechaDesde || null,
        fechaHasta: filtros.fechaHasta || null,
        responsableIds:    filtros.responsableIds.map(r => r.value),
        cuentaContableIds: filtros.cuentaContableIds.map(c => c.value),
        otId: filtros.otSeleccionada?._id || null,
        estadoGasto: filtros.estadoGasto || null
      };
      const resp = await generarInformeFinanciero(payload);
      if (resp?.status === 200) setDatos(resp.data.data);
    } catch (err) {
      console.error('Error generar informe:', err);
      Alertassw('error', 'Error', 'No se pudo generar el informe');
    } finally {
      setLoading(false);
    }
  };

  const limpiarFiltros = () => {
    setFiltros({
      fechaDesde: '', fechaHasta: '',
      responsableIds: [], cuentaContableIds: [],
      otSeleccionada: null, estadoGasto: ''
    });
  };

  // ============================================
  // Exports
  // ============================================
  const imprimir = () => window.print();

  /**
   * Genera PDF del informe completo con jsPDF + autoTable. Layout landscape
   * para que las tablas anchas (por responsable, top proveedores) quepan
   * sin truncar. Cada seccion usa autoTable con `startY = lastAutoTable.finalY`
   * para encadenar sin solaparse y respetar el page break automatico.
   */
  const exportarPDF = () => {
    if (!datos) return;
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const fechaGen = new Date().toLocaleString('es-CO');
      const fmt = (n) => `$ ${Number(n || 0).toLocaleString('es-CO')}`;

      // ============ HEADER ============
      doc.setFontSize(18);
      doc.setTextColor(13, 110, 253);
      doc.setFont('helvetica', 'bold');
      doc.text('Informe Financiero', 40, 50);

      doc.setFontSize(10);
      doc.setTextColor(108, 117, 125);
      doc.setFont('helvetica', 'normal');
      doc.text('Tinvaz · Módulo de Bolsa de Fondos', 40, 66);
      doc.text(`Generado: ${fechaGen}`, pageWidth - 40, 50, { align: 'right' });

      // Linea separadora
      doc.setDrawColor(13, 110, 253);
      doc.setLineWidth(1.5);
      doc.line(40, 78, pageWidth - 40, 78);

      // ============ FILTROS APLICADOS ============
      const filtrosLineas = [];
      if (filtros.fechaDesde) filtrosLineas.push(`Desde ${filtros.fechaDesde}`);
      if (filtros.fechaHasta) filtrosLineas.push(`Hasta ${filtros.fechaHasta}`);
      if (filtros.responsableIds.length) {
        filtrosLineas.push(`Responsables: ${filtros.responsableIds.map(r => r.label).join(', ')}`);
      }
      if (filtros.cuentaContableIds.length) {
        filtrosLineas.push(`Cuentas: ${filtros.cuentaContableIds.map(c => c.label).join(', ')}`);
      }
      if (filtros.otSeleccionada) {
        filtrosLineas.push(`OT ${filtros.otSeleccionada.numeroOrden || ''}`);
      }
      if (filtros.estadoGasto) {
        filtrosLineas.push(`Estado: ${filtros.estadoGasto}`);
      }
      doc.setFontSize(9);
      doc.setTextColor(33, 37, 41);
      doc.setFont('helvetica', 'italic');
      const textoFiltros = filtrosLineas.length
        ? `Filtros aplicados: ${filtrosLineas.join('  ·  ')}`
        : 'Sin filtros aplicados (todos los datos del rango disponible)';
      const lineas = doc.splitTextToSize(textoFiltros, pageWidth - 80);
      doc.text(lineas, 40, 96);
      let y = 96 + (lineas.length * 12);

      // ============ KPIs ============
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(13, 110, 253);
      doc.text('Indicadores clave', 40, y + 6);
      y += 14;

      autoTable(doc, {
        startY: y,
        theme: 'grid',
        styles: { fontSize: 9, halign: 'center', cellPadding: 6 },
        headStyles: { fillColor: [13, 110, 253], textColor: 255, fontStyle: 'bold' },
        head: [['Total Entregado', 'Total Gastado', 'Traslados', 'Saldo', '% Ejecución', 'Días cierre', '# Resp.']],
        body: [[
          fmt(resumen.totalEntregado),
          fmt(resumen.totalGastado),
          fmt(resumen.totalTraslados),
          fmt(resumen.saldoGeneral),
          `${pctEjecucion.toFixed(1)}%`,
          resumen.diasPromedioCierre.toFixed(1),
          resumen.numeroResponsables
        ]]
      });
      y = doc.lastAutoTable.finalY + 16;

      // ============ ALERTAS ============
      if (datos.alertas && datos.alertas.length) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(220, 53, 69);
        doc.text('Alertas', 40, y);
        y += 12;
        autoTable(doc, {
          startY: y,
          theme: 'striped',
          styles: { fontSize: 8.5, cellPadding: 5 },
          headStyles: { fillColor: [220, 53, 69], textColor: 255 },
          head: [['Tipo', 'Severidad', 'Responsable', 'Mensaje', 'Monto']],
          body: datos.alertas.map(a => [
            a.tipo.replace(/_/g, ' '),
            (a.severidad || 'info').toUpperCase(),
            a.responsable || '—',
            a.mensaje,
            a.monto != null ? fmt(a.monto) : '—'
          ]),
          columnStyles: { 4: { halign: 'right' } }
        });
        y = doc.lastAutoTable.finalY + 16;
      }

      // ============ POR RESPONSABLE ============
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(13, 110, 253);
      doc.text('Entregas vs Gastos por responsable', 40, y);
      y += 12;

      autoTable(doc, {
        startY: y,
        theme: 'striped',
        styles: { fontSize: 9, cellPadding: 5 },
        headStyles: { fillColor: [25, 135, 84], textColor: 255 },
        head: [['Responsable', 'Entregado', '+Traslados rec.', 'Gastado', '-Traslados env.', 'Saldo real', '% Ejec.', '#E', '#G']],
        body: [
          ...datos.porResponsable.map(r => [
            r.nombre,
            fmt(r.entregado),
            r.trasladosRecibidos > 0 ? fmt(r.trasladosRecibidos) : '—',
            fmt(r.gastado),
            r.trasladosEnviados  > 0 ? fmt(r.trasladosEnviados)  : '—',
            fmt(r.saldo),
            `${r.porcentajeEjecucion?.toFixed(1) ?? '0.0'}%`,
            r.countEntregas,
            r.countGastos
          ]),
          [
            { content: 'TOTAL', styles: { fontStyle: 'bold' } },
            { content: fmt(resumen.totalEntregado), styles: { fontStyle: 'bold' } },
            { content: '—',                         styles: { fontStyle: 'bold' } },
            { content: fmt(resumen.totalGastado),   styles: { fontStyle: 'bold' } },
            { content: '—',                         styles: { fontStyle: 'bold' } },
            { content: fmt(resumen.saldoGeneral),   styles: { fontStyle: 'bold' } },
            { content: `${pctEjecucion.toFixed(1)}%`, styles: { fontStyle: 'bold' } },
            { content: resumen.numeroEntregas, styles: { fontStyle: 'bold' } },
            { content: resumen.numeroGastos,   styles: { fontStyle: 'bold' } }
          ]
        ],
        columnStyles: {
          1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' },
          4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' },
          7: { halign: 'center' }, 8: { halign: 'center' }
        }
      });
      y = doc.lastAutoTable.finalY + 16;

      // ============ POR CATEGORÍA (clase 5) ============
      if (datos.porCuenta && datos.porCuenta.length) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(13, 110, 253);
        doc.text('Gastos por categoría (clase 5)', 40, y);
        y += 12;
        autoTable(doc, {
          startY: y,
          theme: 'striped',
          styles: { fontSize: 9, cellPadding: 5 },
          headStyles: { fillColor: [13, 110, 253], textColor: 255 },
          head: [['Código', 'Cuenta', 'Categoría', 'Monto', '%', '#']],
          body: datos.porCuenta.map(c => [
            c.codigo, c.nombre, c.categoria,
            fmt(c.monto), `${c.porcentaje.toFixed(1)}%`, c.count
          ]),
          columnStyles: {
            3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'center' }
          }
        });
        y = doc.lastAutoTable.finalY + 16;
      }

      // ============ TOP PROVEEDORES ============
      if (datos.topProveedores && datos.topProveedores.length) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(13, 110, 253);
        doc.text('Top proveedores', 40, y);
        y += 12;
        autoTable(doc, {
          startY: y,
          theme: 'striped',
          styles: { fontSize: 9, cellPadding: 5 },
          headStyles: { fillColor: [102, 16, 242], textColor: 255 },
          head: [['#', 'Proveedor', 'Monto', '# Transacciones']],
          body: datos.topProveedores.map((p, i) => [
            i + 1, p.nombre, fmt(p.monto), p.count
          ]),
          columnStyles: {
            0: { halign: 'center', cellWidth: 30 },
            2: { halign: 'right' },
            3: { halign: 'center' }
          }
        });
        y = doc.lastAutoTable.finalY + 16;
      }

      // ============ TRASLADOS ============
      if (datos.trasladosDetalle && datos.trasladosDetalle.length) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(13, 110, 253);
        doc.text('Traslados entre cuentas / bolsillos', 40, y);
        y += 12;
        autoTable(doc, {
          startY: y,
          theme: 'striped',
          styles: { fontSize: 8.5, cellPadding: 4 },
          headStyles: { fillColor: [13, 202, 240], textColor: 33 },
          head: [['Consec.', 'Fecha', 'Origen', 'Destino', 'Monto', 'Descripción', 'Creado por']],
          body: datos.trasladosDetalle.map(t => [
            t.consecutivo || '—',
            new Date(t.fecha).toLocaleDateString('es-CO'),
            `${t.origenCodigo || ''} ${t.origenNombre}`.trim(),
            `${t.destinoCodigo || ''} ${t.destinoNombre}`.trim(),
            fmt(t.monto),
            (t.descripcion || '').slice(0, 60),
            t.creadoPor
          ]),
          columnStyles: { 4: { halign: 'right' } }
        });
        y = doc.lastAutoTable.finalY + 16;
      }

      // ============ GASTOS POR OT ============
      if (datos.gastosPorOT && datos.gastosPorOT.length) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(13, 110, 253);
        doc.text('Gastos detallados por OT', 40, y);
        y += 12;

        for (const ot of datos.gastosPorOT) {
          const titulo = ot.numeroOT || (ot.otId ? `OT ${String(ot.otId).slice(-6)}` : 'Sin OT asociada');
          autoTable(doc, {
            startY: y,
            theme: 'grid',
            styles: { fontSize: 8.5, cellPadding: 4 },
            headStyles: { fillColor: [253, 126, 20], textColor: 255 },
            head: [[
              { content: `${titulo}  ·  ${ot.count} gasto(s)  ·  Total: ${fmt(ot.total)}`, colSpan: 6 }
            ], ['Fecha', 'Descripción', 'Cuenta', 'Responsable', 'Proveedor', 'Valor']],
            body: ot.gastos.map(g => [
              new Date(g.fecha).toLocaleDateString('es-CO'),
              (g.descripcion || '').slice(0, 50),
              `${g.cuentaCodigo || ''} ${g.cuentaNombre || ''}`.trim(),
              g.responsable,
              g.proveedor || '—',
              fmt(g.valor)
            ]),
            columnStyles: { 5: { halign: 'right' } }
          });
          y = doc.lastAutoTable.finalY + 8;
        }
      }

      // ============ FOOTER (paginacion) ============
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(108, 117, 125);
        doc.setFont('helvetica', 'normal');
        const pageHeight = doc.internal.pageSize.getHeight();
        doc.text(`Página ${i} de ${totalPages}`, pageWidth - 40, pageHeight - 20, { align: 'right' });
        doc.text(`Tinvaz · Informe Financiero · ${fechaGen}`, 40, pageHeight - 20);
      }

      const nombreArchivo = `informe-financiero-${new Date().toISOString().slice(0, 10)}.pdf`;
      doc.save(nombreArchivo);

      Alertassw('success', 'PDF generado', `Archivo "${nombreArchivo}" descargado.`);
    } catch (err) {
      console.error('Error generando PDF:', err);
      Alertassw('error', 'Error', 'No se pudo generar el PDF');
    }
  };

  const exportarExcel = () => {
    Swal.fire({
      icon: 'info', title: 'Excel',
      text: 'La exportación a Excel está en desarrollo.',
      timer: 2200, showConfirmButton: false
    });
  };

  // ============================================
  // Loading
  // ============================================
  if (loading || !datos) {
    return (
      <Container fluid className="py-4">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Generando informe financiero…</p>
        </div>
      </Container>
    );
  }

  // ============================================
  // Datos para gráficos
  // ============================================
  const tendenciaLabels = datos.tendenciaMensual.map(t => formatoMes(t.mes));
  const dataTendencia = {
    labels: tendenciaLabels,
    datasets: [
      {
        label: 'Entregado',
        data: datos.tendenciaMensual.map(t => t.entregado),
        borderColor: '#198754',
        backgroundColor: 'rgba(25,135,84,0.15)',
        fill: true,
        tension: 0.3
      },
      {
        label: 'Gastado',
        data: datos.tendenciaMensual.map(t => t.gastado),
        borderColor: '#0d6efd',
        backgroundColor: 'rgba(13,110,253,0.15)',
        fill: true,
        tension: 0.3
      },
      {
        label: 'Traslados',
        data: datos.tendenciaMensual.map(t => t.traslados),
        borderColor: '#ffc107',
        backgroundColor: 'rgba(255,193,7,0.15)',
        fill: false,
        tension: 0.3
      }
    ]
  };

  // Pie de gasto por responsable: ordenamos por monto gastado desc y
  // tomamos el top 10. Solo incluimos responsables con gasto > 0 para
  // evitar slices vacios que ensucian la leyenda.
  const responsablesConGasto = datos.porResponsable
    .filter(r => r.gastado > 0)
    .sort((a, b) => b.gastado - a.gastado)
    .slice(0, 10);
  const dataGastoPorResponsable = {
    labels: responsablesConGasto.map(r => r.nombre),
    datasets: [{
      data: responsablesConGasto.map(r => r.gastado),
      backgroundColor: [
        '#0d6efd','#198754','#ffc107','#dc3545','#6610f2',
        '#fd7e14','#20c997','#6c757d','#0dcaf0','#d63384'
      ]
    }]
  };

  const { resumen } = datos;
  const pctEjecucion = resumen.porcentajeEjecucionGeneral || 0;

  return (
    <Container fluid className="py-4 w-100">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <h2 className="text-primary fw-bold">
            <FaFileInvoiceDollar className="me-2" />
            Informe Financiero
          </h2>
          <p className="text-muted">Vista administrativa: entregas, gastos, traslados y ejecución por responsable.</p>
        </Col>
        <Col xs="auto">
          <div className="d-flex gap-2 no-print">
            <Button variant="outline-primary" onClick={imprimir}><FaPrint className="me-1" /> Imprimir</Button>
           {/* <Button variant="outline-success" onClick={exportarExcel}><FaDownload className="me-1" /> Excel</Button>*/}
            <Button variant="outline-danger" onClick={exportarPDF}><FaDownload className="me-1" /> PDF</Button>
          </div>
        </Col>
      </Row>

      {/* Filtros */}
      <Card className="mb-4 shadow-sm no-print w-100">
        <Card.Header className="bg-light w-100">
          <h5 className="mb-0">
            <FaCalendarAlt className="me-2" />
            Filtros del Informe
            {cargandoCatalogos && <Spinner animation="border" size="sm" className="ms-2" />}
          </h5>
        </Card.Header>
        <Card.Body className="w-100">
          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fw-semibold">Desde</Form.Label>
                <Form.Control
                  type="date"
                  value={filtros.fechaDesde}
                  onChange={(e) => handleFiltro('fechaDesde', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fw-semibold">Hasta</Form.Label>
                <Form.Control
                  type="date"
                  value={filtros.fechaHasta}
                  onChange={(e) => handleFiltro('fechaHasta', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fw-semibold">Estado del gasto</Form.Label>
                <Form.Select
                  value={filtros.estadoGasto}
                  onChange={(e) => handleFiltro('estadoGasto', e.target.value)}
                >
                  {ESTADOS_GASTO.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fw-semibold">Orden de Trabajo</Form.Label>
                <SelectorOT
                  value={filtros.otSeleccionada}
                  onChange={(otRaw) => handleFiltro('otSeleccionada', otRaw)}
                  placeholder="Buscar OT por consecutivo o cliente…"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Responsables</Form.Label>
                <Select
                  isMulti
                  options={responsablesOpt}
                  value={filtros.responsableIds}
                  onChange={(v) => handleFiltro('responsableIds', v || [])}
                  placeholder="Todos los responsables…"
                  noOptionsMessage={() => 'Sin coincidencias'}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Cuentas contables (fuente / bolsillo)</Form.Label>
                <Select
                  isMulti
                  options={cuentasOpt}
                  value={filtros.cuentaContableIds}
                  onChange={(v) => handleFiltro('cuentaContableIds', v || [])}
                  placeholder="Todas las cuentas…"
                  noOptionsMessage={() => 'Sin coincidencias'}
                />
              </Form.Group>
            </Col>
            <Col xs={12} className="d-flex gap-2 justify-content-end">
              <Button variant="outline-secondary" onClick={limpiarFiltros}>Limpiar</Button>
              <Button variant="primary" onClick={generar}>Generar Informe</Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Alertas */}
      {datos.alertas?.length > 0 && (
        <div className="mb-4 no-print">
          {datos.alertas.map((a, idx) => (
            <Alert key={idx} variant={ALERT_VARIANT[a.severidad] || 'info'} className="py-2 mb-2">
              <FaExclamationTriangle className="me-2" />
              <strong>{a.tipo.replace(/_/g, ' ')}:</strong> {a.mensaje}
              {a.monto != null && <span className="ms-2 fw-bold">{formatoCOP(a.monto)}</span>}
            </Alert>
          ))}
        </div>
      )}

      {/* KPIs */}
      <Row className="mb-4 g-2">
        <Col md={2}>
          <Card className="shadow-sm  border-start border-success border-4 w-100">
            <Card.Body>
              <h6 className="text-muted mb-2"><FaArrowDown className="me-1" />Total Entregado</h6>
              <h4 className="fw-bold text-success mb-0">{formatoCOP(resumen.totalEntregado)}</h4>
              <small className="text-muted">{resumen.numeroEntregas} entrega(s)</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="shadow-sm  border-start border-primary border-4 w-100">
            <Card.Body>
              <h6 className="text-muted mb-2"><FaArrowUp className="me-1" />Total Gastado</h6>
              <h4 className="fw-bold text-primary mb-0">{formatoCOP(resumen.totalGastado)}</h4>
              <small className="text-muted">{resumen.numeroGastos} gasto(s)</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="shadow-sm  border-start border-info border-4 w-100">
            <Card.Body className="w-100">
              <h6 className="text-muted mb-2"><FaExchangeAlt className="me-1" />Traslados</h6>
              <h4 className="fw-bold text-info mb-0">{formatoCOP(resumen.totalTraslados)}</h4>
              <small className="text-muted">{resumen.numeroTraslados} traslado(s)</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className={`shadow-sm w-100 border-start border-4 ${resumen.saldoGeneral >= 0 ? 'border-warning' : 'border-danger'}`}>
            <Card.Body className="w-100">
              <h6 className="text-muted mb-2">Saldo General</h6>
              <h4 className={`fw-bold mb-0 ${resumen.saldoGeneral >= 0 ? 'text-warning' : 'text-danger'}`}>
                {formatoCOP(resumen.saldoGeneral)}
              </h4>
              <small className="text-muted">Entregado − Gastado</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="shadow-sm  border-start border-secondary border-4 w-100">
            <Card.Body className="w-100">
              <h6 className="text-muted mb-2"><FaChartLine className="me-1" />% Ejecución</h6>
              <h4 className="fw-bold mb-0">{pctEjecucion.toFixed(1)}%</h4>
              <ProgressBar
                now={Math.min(100, pctEjecucion)}
                variant={pctEjecucion > 100 ? 'danger' : pctEjecucion > 80 ? 'success' : 'warning'}
                className="mt-1"
                style={{ height: 6 }}
              />
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="shadow-sm  border-start border-dark border-4 w-100">
            <Card.Body className="w-100">
              <h6 className="text-muted mb-2"><FaClock className="me-1" />Días promedio cierre</h6>
              <h4 className="fw-bold mb-0">{resumen.diasPromedioCierre.toFixed(1)}</h4>
              <small className="text-muted">
                <FaUsers className="me-1" />{resumen.numeroResponsables} responsable(s)
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tendencia mensual + Pie por categoria */}
      <Row className="mb-4 g-3">
        <Col md={8}>
          <Card className="shadow-sm  w-100">
            <Card.Header className="bg-light w-100">
              <h5 className="mb-0"><FaChartLine className="me-2" />Tendencia mensual</h5>
            </Card.Header>
            <Card.Body className="w-100">
              {datos.tendenciaMensual.length === 0 ? (
                <Alert variant="info" className="text-center mb-0">Sin movimientos en el rango.</Alert>
              ) : (
                <Line data={dataTendencia} options={{
                  responsive: true,
                  plugins: { legend: { position: 'top' } },
                  scales: { y: { beginAtZero: true } }
                }} />
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm  w-100">
            <Card.Header className="bg-light w-100">
              <h5 className="mb-0"><FaUsers className="me-2" />Gasto por responsable</h5>
              <small className="text-muted">Quién gasta más en el periodo (top 10)</small>
            </Card.Header>
            <Card.Body className="w-100">
              {responsablesConGasto.length === 0 ? (
                <Alert variant="info" className="text-center mb-0">Sin gastos en el rango.</Alert>
              ) : (
                <Pie data={dataGastoPorResponsable} options={{
                  responsive: true,
                  plugins: {
                    legend: { position: 'bottom' },
                    tooltip: {
                      callbacks: {
                        label: (ctx) => `${ctx.label}: ${formatoCOP(ctx.raw)}`
                      }
                    }
                  }
                }} />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Entregas vs Gastos por responsable */}
      <Card className="mb-4 shadow-sm w-100">
        <Card.Header className="bg-light w-100">
          <h5 className="mb-0"><FaUsers className="me-2" />Entregas vs Gastos por responsable</h5>
        </Card.Header>
        <Card.Body className="w-100">
          {datos.porResponsable.length === 0 ? (
            <Alert variant="info" className="text-center mb-0">Sin datos.</Alert>
          ) : (
            <Table hover responsive>
              <thead className="table-light">
                <tr>
                  <th>Responsable</th>
                  <th className="text-end">Entregado</th>
                  <th className="text-end text-success">+Traslados rec.</th>
                  <th className="text-end">Gastado</th>
                  <th className="text-end text-warning">-Traslados env.</th>
                  <th className="text-end">Saldo real</th>
                  <th style={{ width: '160px' }} title="Gastado ÷ (Entregado + Traslados rec. − Traslados env.)">% Ejecución</th>
                  <th className="text-center">#E / #G</th>
                </tr>
              </thead>
              <tbody>
                {datos.porResponsable.map(r => (
                  <tr key={r._id}>
                    <td><strong>{r.nombre}</strong></td>
                    <td className="text-end text-success fw-bold">{formatoCOP(r.entregado)}</td>
                    <td className="text-end text-success">{r.trasladosRecibidos > 0 ? `+${formatoCOP(r.trasladosRecibidos)}` : '—'}</td>
                    <td className="text-end text-primary fw-bold">{formatoCOP(r.gastado)}</td>
                    <td className="text-end text-warning">{r.trasladosEnviados > 0 ? `-${formatoCOP(r.trasladosEnviados)}` : '—'}</td>
                    <td className={`text-end fw-bold ${r.saldo < 0 ? 'text-danger' : 'text-warning'}`}>
                      {formatoCOP(r.saldo)}
                    </td>
                    <td>
                      <ProgressBar
                        now={Math.min(100, r.porcentajeEjecucion)}
                        label={`${r.porcentajeEjecucion.toFixed(0)}%`}
                        variant={r.porcentajeEjecucion > 100 ? 'danger' : r.porcentajeEjecucion > 80 ? 'success' : r.porcentajeEjecucion > 30 ? 'warning' : 'secondary'}
                      />
                    </td>
                    <td className="text-center">
                      <Badge bg="success" className="me-1">{r.countEntregas}</Badge>
                      <Badge bg="primary">{r.countGastos}</Badge>
                    </td>
                  </tr>
                ))}
                <tr className="table-active fw-bold">
                  <td>TOTAL</td>
                  <td className="text-end">{formatoCOP(resumen.totalEntregado)}</td>
                  <td className="text-end">—</td>
                  <td className="text-end">{formatoCOP(resumen.totalGastado)}</td>
                  <td className="text-end">—</td>
                  <td className="text-end">{formatoCOP(resumen.saldoGeneral)}</td>
                  <td>{pctEjecucion.toFixed(1)}%</td>
                  <td></td>
                </tr>
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Gastos por cuenta categoría */}
      <Row className="g-3 mb-4">
        <Col md={7}>
          <Card className="shadow-sm  w-100">
            <Card.Header className="bg-light w-100">
              <h5 className="mb-0">Gastos por categoría</h5>
            </Card.Header>
            <Card.Body className="w-100">
              {datos.porCuenta.length === 0 ? (
                <Alert variant="info" className="text-center mb-0">Sin gastos.</Alert>
              ) : (
                <Table hover responsive size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>Código</th>
                      <th>Categoría</th>
                      <th className="text-end">Monto</th>
                      <th style={{ width: 140 }}>%</th>
                      <th className="text-center">#</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.porCuenta.map(c => (
                      <tr key={c._id}>
                        <td><code>{c.codigo}</code></td>
                        <td><small>{c.nombre}</small></td>
                        <td className="text-end fw-bold">{formatoCOP(c.monto)}</td>
                        <td>
                          <div className="progress" style={{ height: 16 }}>
                            <div className="progress-bar bg-primary" style={{ width: `${c.porcentaje}%` }}>
                              {c.porcentaje.toFixed(1)}%
                            </div>
                          </div>
                        </td>
                        <td className="text-center">{c.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={5}>
          <Card className="shadow-sm w-100">
            <Card.Header className="bg-light w-100">
              <h5 className="mb-0"><FaBuilding className="me-2" />Top proveedores</h5>
            </Card.Header>
            <Card.Body className="w-100">
              {datos.topProveedores.length === 0 ? (
                <Alert variant="info" className="text-center mb-0">Sin proveedores en el rango.</Alert>
              ) : (
                <Table hover responsive size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>Proveedor</th>
                      <th className="text-end">Monto</th>
                      <th className="text-center">#</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.topProveedores.map(p => (
                      <tr key={p._id}>
                        <td><small>{p.nombre}</small></td>
                        <td className="text-end fw-bold">{formatoCOP(p.monto)}</td>
                        <td className="text-center">{p.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Traslados detalle */}
      {datos.trasladosDetalle.length > 0 && (
        <Card className="mb-4 shadow-sm w-100">
          <Card.Header className="bg-light w-100">
            <h5 className="mb-0"><FaExchangeAlt className="me-2" />Traslados entre cuentas / bolsillos</h5>
          </Card.Header>
          <Card.Body className="w-100">
            <Table hover responsive size="sm">
              <thead className="table-light">
                <tr>
                  <th>Consecutivo</th>
                  <th>Fecha</th>
                  <th>Origen</th>
                  <th>Destino</th>
                  <th className="text-end">Monto</th>
                  <th>Descripción</th>
                  <th>Creado por</th>
                </tr>
              </thead>
              <tbody>
                {datos.trasladosDetalle.map(t => (
                  <tr key={t._id}>
                    <td><small className="text-muted">{t.consecutivo || '—'}</small></td>
                    <td>{new Date(t.fecha).toLocaleDateString('es-CO')}</td>
                    <td>
                      <small>
                        {t.origenCodigo && <code className="me-1">{t.origenCodigo}</code>}
                        {t.origenNombre}
                      </small>
                    </td>
                    <td>
                      <small>
                        {t.destinoCodigo && <code className="me-1">{t.destinoCodigo}</code>}
                        {t.destinoNombre}
                      </small>
                    </td>
                    <td className="text-end fw-bold">{formatoCOP(t.monto)}</td>
                    <td><small>{t.descripcion || '—'}</small></td>
                    <td><small>{t.creadoPor}</small></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Gastos por OT — plegable para no alargar el informe */}
      {datos.gastosPorOT && datos.gastosPorOT.length > 0 && (
        <Accordion className="mb-4 no-print">
          <Accordion.Item eventKey="0">
            <Accordion.Header>
              <FaFolderOpen className="me-2 text-warning" />
              <strong className="me-2">Gastos por OT</strong>
              <Badge bg="secondary" className="me-2">{datos.gastosPorOT.length} OT(s)</Badge>
              <small className="text-muted">expandir para ver detalle</small>
            </Accordion.Header>
            <Accordion.Body>
              <Accordion alwaysOpen={false}>
                {datos.gastosPorOT.map((ot, idx) => (
                  <Accordion.Item key={ot.otId || `sinot-${idx}`} eventKey={`${idx}`}>
                    <Accordion.Header>
                      <div className="d-flex justify-content-between w-100 me-3 align-items-center">
                        <div>
                          <strong>{ot.numeroOT || (ot.otId ? `OT ${ot.otId.slice(-6)}` : 'Sin OT asociada')}</strong>
                          <Badge bg="secondary" className="ms-2">{ot.count} gasto(s)</Badge>
                        </div>
                        <strong className="text-primary">{formatoCOP(ot.total)}</strong>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      <Table hover responsive size="sm" className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Fecha</th>
                            <th>Descripción</th>
                            <th>Cuenta</th>
                            <th>Categoría</th>
                            <th>Responsable</th>
                            <th>Proveedor</th>
                            <th className="text-end">Valor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ot.gastos.map(g => (
                            <tr key={g._id}>
                              <td>{new Date(g.fecha).toLocaleDateString('es-CO')}</td>
                              <td><small>{g.descripcion}</small></td>
                              <td>
                                {g.cuentaCodigo && <code className="me-1">{g.cuentaCodigo}</code>}
                                <small>{g.cuentaNombre || '—'}</small>
                              </td>
                              <td><small className="text-muted">{g.categoria || '—'}</small></td>
                              <td><small>{g.responsable}</small></td>
                              <td><small>{g.proveedor || '—'}</small></td>
                              <td className="text-end fw-bold">{formatoCOP(g.valor)}</td>
                            </tr>
                          ))}
                          <tr className="table-active fw-bold">
                            <td colSpan="6" className="text-end">Subtotal</td>
                            <td className="text-end">{formatoCOP(ot.total)}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      )}

      {/* Pie impresión */}
      <div className="mt-4 text-center text-muted print-only">
        <hr />
        <small>
          Informe generado el {new Date().toLocaleString('es-CO')}<br />
          Tinvaz · Módulo Financiero
        </small>
      </div>

      {/* Estilos impresión */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        @media screen { .print-only { display: none; } }
      `}</style>
    </Container>
  );
};

export default InformeFinanciero;
